import { Badge, Button, DataTable } from 'lift-ui';
import type { TableAction, TableColumn } from 'lift-ui';
import { Play, RefreshCw } from 'lucide-react';
import { useJobsAccess, useRecurringTasks, useRunRecurringTask } from '../hooks';
import type { RecurringTask } from '../types';
import { formatTimestamp } from '../utils';

export const RecurringTasksTab = () => {
  const { canEdit } = useJobsAccess();
  const { data, isLoading, isFetching, refetch } = useRecurringTasks();
  const runTask = useRunRecurringTask();

  const columns: TableColumn<RecurringTask>[] = [
    {
      key: 'id',
      header: 'Task',
      accessor: 'id',
      render: (_value, row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.id}</span>
          <span className="text-xs text-muted-foreground">{row.job_class_name ?? row.command}</span>
        </div>
      ),
    },
    { key: 'schedule', header: 'Schedule', accessor: 'schedule' },
    {
      key: 'queue_name',
      header: 'Queue',
      accessor: 'queue_name',
      render: (_value, row) =>
        row.queue_name ? <Badge variant="greyLight">{row.queue_name}</Badge> : '—',
    },
    {
      key: 'last_enqueued_at',
      header: 'Last run',
      accessor: 'last_enqueued_at',
      render: (_value, row) => (
        <span className="text-xs whitespace-nowrap">{formatTimestamp(row.last_enqueued_at)}</span>
      ),
    },
    {
      key: 'next_time',
      header: 'Next run',
      accessor: 'next_time',
      render: (_value, row) => (
        <span className="text-xs whitespace-nowrap">{formatTimestamp(row.next_time)}</span>
      ),
    },
  ];

  const actions: TableAction<RecurringTask>[] = canEdit
    ? [
        {
          label: 'Run now',
          icon: <Play size={14} />,
          variant: 'outline',
          disabled: (row) => !row.runnable || runTask.isPending,
          onClick: (row) => runTask.mutate(row.id),
        },
      ]
    : [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      <DataTable<RecurringTask>
        data={data ?? []}
        columns={columns}
        actions={actions}
        showActions={actions.length > 0}
        loading={isLoading}
        emptyMessage="No recurring tasks are configured"
        stickyHeader
      />
    </div>
  );
};

export default RecurringTasksTab;
